import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, unwrap } from '../api/client';
import { useAgent, useCatalog } from '../api/hooks';
import { ErrorNote, Loading, PageHead } from '../components/ui';

type Draft = {
  matricule: string;
  lastName: string;
  firstName: string;
  birthDate: string;
  email: string;
  phone: string;
  statusId: string;
  nir: string;
  iban: string;
};

const empty: Draft = { matricule: '', lastName: '', firstName: '', birthDate: '', email: '', phone: '', statusId: '', nir: '', iban: '' };

/** Création et modification d'un agent (DRH). Les champs sensibles ne sont envoyés que s'ils sont saisis. */
export function AgentFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const existing = useAgent(id ?? '');
  const statuses = useCatalog('agent-statuses');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);

  const form: Draft =
    draft ??
    (editing && existing.data
      ? {
          matricule: existing.data.matricule,
          lastName: existing.data.lastName,
          firstName: existing.data.firstName,
          birthDate: existing.data.birthDate ?? '',
          email: existing.data.email ?? '',
          phone: existing.data.phone ?? '',
          statusId: existing.data.statusId ?? '',
          nir: '',
          iban: '',
        }
      : empty);
  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setDraft({ ...form, [k]: e.target.value });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        matricule: form.matricule,
        lastName: form.lastName,
        firstName: form.firstName,
        birthDate: form.birthDate || null,
        email: form.email || null,
        phone: form.phone || null,
        statusId: form.statusId || null,
        ...(form.nir ? { nir: form.nir } : {}),
        ...(form.iban ? { iban: form.iban } : {}),
      };
      return editing
        ? unwrap(await api.PATCH('/api/v1/agents/{id}', { params: { path: { id: id! } }, body }))
        : unwrap(await api.POST('/api/v1/agents', { body }));
    },
    onSuccess: (agent) => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void navigate(`/agents/${agent.id}`);
    },
  });

  if (editing && existing.isPending) return <Loading what="Ouverture du dossier" />;
  if (editing && existing.error) return <ErrorNote error={existing.error} />;

  return (
    <>
      <PageHead eyebrow="Ressources humaines" title={editing ? 'Modifier un agent' : 'Nouvel agent'} actions={<Link to={editing ? `/agents/${id}` : '/agents'}>← Retour</Link>}>
        {editing ? 'Laissez NIR et IBAN vides pour ne pas les changer.' : 'Le matricule est unique dans la collectivité.'}
      </PageHead>
      <form
        className="card"
        style={{ maxWidth: 820 }}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="form-grid">
          <div className="field">
            <label htmlFor="a-matricule">Matricule</label>
            <input id="a-matricule" required value={form.matricule} onChange={set('matricule')} />
          </div>
          <div className="field">
            <label htmlFor="a-status">Statut</label>
            <select id="a-status" value={form.statusId} onChange={set('statusId')}>
              <option value="">— Non renseigné —</option>
              {(statuses.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="a-last">Nom</label>
            <input id="a-last" required value={form.lastName} onChange={set('lastName')} />
          </div>
          <div className="field">
            <label htmlFor="a-first">Prénom</label>
            <input id="a-first" required value={form.firstName} onChange={set('firstName')} />
          </div>
          <div className="field">
            <label htmlFor="a-birth">Date de naissance</label>
            <input id="a-birth" type="date" value={form.birthDate} onChange={set('birthDate')} />
          </div>
          <div className="field">
            <label htmlFor="a-email">Courriel</label>
            <input id="a-email" type="email" value={form.email} onChange={set('email')} />
          </div>
          <div className="field">
            <label htmlFor="a-phone">Téléphone</label>
            <input id="a-phone" value={form.phone} onChange={set('phone')} />
          </div>
        </div>
        <h3>Données sensibles</h3>
        <p className="muted small">Chiffrées à l'enregistrement, jamais affichées en clair dans les listes. Leur consultation est journalisée.</p>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="a-nir">NIR (numéro de sécurité sociale)</label>
            <input id="a-nir" value={form.nir} onChange={set('nir')} placeholder={editing && existing.data?.nirMasked ? existing.data.nirMasked : '13 à 15 chiffres'} autoComplete="off" />
          </div>
          <div className="field">
            <label htmlFor="a-iban">IBAN</label>
            <input id="a-iban" value={form.iban} onChange={set('iban')} placeholder={editing && existing.data?.ibanMasked ? existing.data.ibanMasked : 'FR76 …'} autoComplete="off" />
          </div>
        </div>
        <ErrorNote error={save.error} />
        <div className="actions">
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>
            {editing ? 'Enregistrer' : 'Créer le dossier'}
          </button>
        </div>
      </form>
    </>
  );
}
